import screenfull from "screenfull";
import Player from "video.js/dist/types/player";

import { isSmartPhone } from "../../../utility";
import { blurFromActiveElement } from "./blurFromActiveElement";
import { getVideoJsComponent } from "./utils";

import type { THEOPlayerRuntime } from "./theoplayerRuntime";

export const fullscreenClassName = "fullscreen-mode-enabled";

export const getFullScreenModeButton = (runtime: THEOPlayerRuntime) => {
  const VideoJsButton = getVideoJsComponent("FullscreenToggle", runtime);

  class FullScreenModeButton extends VideoJsButton {
    constructor(player: Player, options = {}) {
      super(player, options);

      // フルスクリーン状態の変化を監視するイベントリスナーを登録！
      document.addEventListener(
        "fullscreenchange",
        this.handleFullscreenChange
      );

      // screenfullのchangeイベントでクラスを同期
      // escでフルスクリーンを解除すると、状態とクラスが同期しないため
      if (screenfull.isEnabled) {
        screenfull.on("change", this.handleChangeForScreenfull);
      }

      // プレイヤーが破棄される時にリスナーも解除！
      player.on("dispose", () => {
        document.removeEventListener(
          "fullscreenchange",
          this.handleFullscreenChange
        );

        if (screenfull.isEnabled) {
          screenfull.off("change", this.handleChangeForScreenfull);
        }
      });
    }

    handleChangeForScreenfull = () => {
      const streamingEl = document.getElementById("Streaming");
      if (!streamingEl) return;

      if (
        this.player().isFullscreen() ||
        (screenfull.isEnabled && screenfull.isFullscreen)
      ) {
        streamingEl.classList.add(fullscreenClassName);
        blurFromActiveElement();
      } else {
        streamingEl.classList.remove(fullscreenClassName);
      }
    };

    handleFullscreenChange = () => {
      const btn = this.el();
      const isFullscreen = document.fullscreenElement !== null;

      if (!btn) return;

      if (isFullscreen) {
        blurFromActiveElement();
      }

      // fullscreenIconとendFullIconのSVGを定義
      const fullscreenIcon = `
      <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M10.8681 20.8489H8V28H15.1511V25.1319H10.8681V20.8489ZM8 15.1511H10.8681V10.8681H15.1511V8H8V15.1511ZM25.1319 25.1319H20.8489V28H28V20.8489H25.1319V25.1319ZM20.8489 8V10.8681H25.1319V15.1511H28V8H20.8489Z" fill="white"/>
      </svg>
    `;
      const endFullIcon = `
      <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M8 21V23.8681H12.283V28.1511H15.1511V21H8Z" fill="white"/>
        <path d="M20.9993 28.1511L23.8674 28.1511L23.8674 23.8681L28.1504 23.8681L28.1504 21L20.9993 21L20.9993 28.1511Z" fill="white"/>
        <path d="M28 15L28 12.1925L23.8075 12.1925L23.8075 8L21 8L21 15L28 15Z" fill="white"/>
        <path d="M15 8L12.1925 8L12.1925 12.1925L8 12.1925L8 15L15 15L15 8Z" fill="white"/>
      </svg>
    `;

      // アイコン切り替え
      btn.innerHTML = isFullscreen
        ? `${endFullIcon}<span class="vjs-control-text">フルスクリーン解除</span>`
        : `${fullscreenIcon}<span class="vjs-control-text">フルスクリーンモード</span>`;
    };

    handleClick() {
      if (isSmartPhone()) {
        // スマホの場合はネイティブのフルスクリーンAPIを使用する
        if (this.player().isFullscreen()) {
          this.player().exitFullscreen();
        } else {
          this.player().requestFullscreen();
        }
        return;
      }

      const streamingEl = document.getElementById("Streaming");
      streamingEl?.classList.toggle(fullscreenClassName);

      if (screenfull.isFullscreen) {
        handleExitFullscreen();
      } else if (this.options(undefined).fullscreenElementRef) {
        handleFullscreen(this.options(undefined).fullscreenElementRef);
      }
    }

    createEl() {
      const fullscreenIcon = `
      <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M10.8681 20.8489H8V28H15.1511V25.1319H10.8681V20.8489ZM8 15.1511H10.8681V10.8681H15.1511V8H8V15.1511ZM25.1319 25.1319H20.8489V28H28V20.8489H25.1319V25.1319ZM20.8489 8V10.8681H25.1319V15.1511H28V8H20.8489Z" fill="white"/>
      </svg>
    `;
      const el = super.createEl("button", {
        className:
          "vjs-custom-fullscreen-mode-button vjs-custom-button vjs-control vjs-button",
        innerHTML: `${fullscreenIcon}<span class="vjs-control-text">フルスクリーンモード</span>`,
      });
      return el;
    }
  }

  const handleFullscreen = (ref: React.RefObject<HTMLDivElement>) => {
    const elem = ref.current;
    if (!elem) return;

    if (screenfull.isEnabled && elem) {
      screenfull.request(elem);
    }
  };

  const handleExitFullscreen = () => {
    if (screenfull.isEnabled) {
      screenfull.exit();
    }
  };
  return FullScreenModeButton;
};
